import { apiDelete, apiGet, apiPost, apiPut } from '.';
import {
  NeedsAttentionItem,
  NewReservation,
  PaginateReservationParams,
  RemapReservationSlot,
  Reservation,
  ReservationAttendance,
} from '@go-shared/types/reservation';
import qs from 'query-string';
import { PaginationResult } from '@go-shared/types/pagination';
import { ReservePaymentMethod, ReservePaymentType } from '@go-shared/lib/reserve';
import { ReserveTimeline } from '@go-shared/types/reserve';

export async function get(propertyId: string, reservationId: string): Promise<Reservation> {
  return apiGet(`/property/${propertyId}/reservation/${reservationId}`);
}

export async function getRelatedReservations(
  propertyId: string,
  reservationId: string
): Promise<Reservation[]> {
  return apiGet(`/property/${propertyId}/reservation/${reservationId}/related`);
}

export async function findOngoing(
  propertyId: string,
  from?: string,
  to?: string,
  includeCancelled?: boolean
): Promise<ReserveTimeline[]> {
  return apiGet(`/property/${propertyId}/reservation/ongoing`, {
    from,
    to,
    includeCancelled,
  });
}

export async function findFlagged(propertyId: string): Promise<NeedsAttentionItem[]> {
  return apiGet(`/property/${propertyId}/reservation/flagged`);
}

export async function findUpcomingByTemplate(
  propertyId: string,
  templateId: string
): Promise<Reservation[]> {
  return apiGet(`/property/${propertyId}/reservation/upcoming/${templateId}`);
}

export async function remapSlots(
  propertyId: string,
  mappings: RemapReservationSlot[]
): Promise<void> {
  return apiPost(`/property/${propertyId}/reservation/remap-slots`, mappings);
}

export async function paginateReservations(
  params: PaginateReservationParams
): Promise<PaginationResult<Reservation>> {
  return apiGet(`/property/${params.propertyId}/reservation/paginate?${qs.stringify(params)}`);
}

export async function create(reservation: NewReservation): Promise<Reservation> {
  return apiPost(`/property/${reservation.property}/reservation`, reservation);
}

export async function update(reservation: Reservation): Promise<Reservation> {
  const { _id, property } = reservation;
  return apiPut(`/property/${property}/reservation/${_id}`, reservation);
}

export async function toggleStatus(reservation: Reservation): Promise<Reservation> {
  const { _id, property } = reservation;
  return apiPost(`/property/${property}/reservation/${_id}/toggle-status`);
}

export async function changeAttendance(
  reservation: Reservation,
  attendance: ReservationAttendance | undefined
): Promise<Reservation> {
  const { _id, property } = reservation;
  return apiPost(`/property/${property}/reservation/${_id}/change-attendance`, { attendance });
}

export async function sendConfirmationEmail(reservation: Reservation): Promise<void> {
  const { _id, property } = reservation;
  return apiPost(`/property/${property}/reservation/${_id}/send-confirmation`);
}

export async function exportTransactionsToCsv(
  propertyId: string,
  from?: string,
  to?: string,
  includeCancelled?: boolean
) {
  return apiGet<{ location: string }>(`/property/${propertyId}/reservation/export-csv`, {
    from,
    to,
    includeCancelled,
  });
}

export async function createManualPayment(
  reservation: Reservation,
  body: {
    type: ReservePaymentType;
    method: ReservePaymentMethod;
    amount: number;
    notes: string;
  }
): Promise<Reservation> {
  const { _id, property } = reservation;

  return apiPost(`/property/${property}/reservation/${_id}/manual-payment`, body);
}

export async function openTerminalPayment(
  reservation: Reservation,
  paymentIntentId: string,
  readerId: string
): Promise<void> {
  const { _id, property } = reservation;
  return apiPost(`/property/${property}/reservation/${_id}/open-terminal`, {
    paymentIntentId,
    readerId,
  });
}

export async function showTerminalDisplay(
  reservation: Reservation,
  paymentIntentId: string,
  readerId: string
): Promise<void> {
  const { _id, property } = reservation;
  return apiPost(`/property/${property}/reservation/${_id}/show-terminal-display`, {
    paymentIntentId,
    readerId,
  });
}

export async function cancelTerminalPayment(
  reservation: Reservation,
  readerId: string
): Promise<void> {
  const { _id, property } = reservation;
  return apiPost(`/property/${property}/reservation/${_id}/cancel-terminal`, { readerId });
}

export async function deleteManualPayment(
  reservation: Reservation,
  paymentId: string
): Promise<Reservation> {
  const { _id, property } = reservation;

  return apiDelete(`/property/${property}/reservation/${_id}/manual-payment/${paymentId}`);
}

export async function changePaymentNotes(
  reservation: Reservation,
  paymentId: string,
  notes: string
): Promise<Reservation> {
  const { _id, property } = reservation;

  return apiPost(`/property/${property}/reservation/${_id}/payment/${paymentId}/notes`, { notes });
}
